import React, { useState } from "react";
import { ChevronUp, ChevronDown, X } from "lucide-react";
import { Agent } from "../../model_gen/agent_base";

interface ContextSectionProps {
  accumulatedContext: Agent[];
  currentMentions: Agent[];
  onRemoveFromContext: (agentId: number) => void;
  onRemoveFromCurrentMentions: (agentId: number) => void;
}

export const ContextSection: React.FC<ContextSectionProps> = ({
  accumulatedContext,
  currentMentions,
  onRemoveFromContext,
  onRemoveFromCurrentMentions
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  
  // Filter out duplicates from currentMentions
  const uniqueCurrentMentions = currentMentions.filter(
    mention => !accumulatedContext.some(a => a.id === mention.id)
  );
  
  const totalCount = accumulatedContext.length + uniqueCurrentMentions.length;
  
  if (totalCount === 0) return null;
  
  return (
    <div className="border-t border-b border-border bg-muted/30">
      {/* Collapsible header */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Context</span>
          <span className="text-xs text-muted-foreground">
            ({totalCount} {totalCount === 1 ? "agent" : "agents"})
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        )}
      </button>

      {/* Collapsible content */}
      {isExpanded && (
        <div className="px-4 pb-3 flex flex-wrap gap-2">
          {/* Show accumulated context first */}
          {accumulatedContext.map((agent) => {
            // Use displayName if available (for actions), otherwise use agent_name
            const displayName = (agent as any).displayName || agent.agent_name;
            return (
              <div
                key={`accumulated-${agent.id}`}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-background rounded-lg border border-border group hover:border-maitai-green/50 transition-all"
              >
                <span className="text-sm font-medium">{displayName}</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveFromContext(agent.id);
                  }}
                  className="text-muted-foreground"
                  aria-label="Remove from context"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
          
          {/* Show current mentions that aren't already in accumulated context */}
          {uniqueCurrentMentions.map((agent) => {
            const displayName = (agent as any).displayName || agent.agent_name;
            return (
              <div
                key={`current-${agent.id}`}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-background rounded-lg border border-maitai-green/50 group hover:border-maitai-green transition-all"
              >
                <span className="text-sm font-medium">{displayName}</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveFromCurrentMentions(agent.id);
                  }}
                  className="text-muted-foreground"
                  aria-label="Remove from context"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
